import { service } from '@ember/service'
import Component from '@glimmer/component'
import { use } from 'ember-resources'
import { keepLatest } from 'reactiveweb/keep-latest'
import { trackedFunction } from 'reactiveweb/function'
import TeacherFeedbackCard from 'teacher-ui-client/components/writing-legends/teacher-feedback-card.gts'
import TeacherFeedbackForm from 'teacher-ui-client/components/writing-legends/teacher-feedback-form.gts'
import type WritingLegendsSubmissionService from 'teacher-ui-client/services/writing-legends-submission'
import type { ThemeKeys } from 'teacher-ui-client/utils/themes'
import { htmlSafe } from '@ember/template'
import { t } from 'ember-intl'

interface Signature {
  Element: HTMLDivElement
  Args: {
    themeKey: ThemeKeys | undefined
    studentId?: string
    resultId?: string
    snapshotId?: string
  }
}

interface Feedback {
  feedback_id: string
  student_id: string
  result_id: string
  activity_result_snapshot_id: string
  is_seen: boolean
  is_deleted: boolean
  feedback_text: string
  mark: number
  sticker: Nullable<string>
  created_date: number
  teacher_full_name: string
}

export class TeacherFeedbacksSection extends Component<Signature> {
  @service writingLegendsSubmission!: WritingLegendsSubmissionService

  fetchData = trackedFunction(this, () => {
    if (!this.args.studentId || !this.args.resultId) {
      return []
    }
    return this.writingLegendsSubmission.fetchData<Feedback[]>(
      this.writingLegendsSubmission.buildFeedbackUrl(this.args.studentId, this.args.resultId),
    )
  })

  @use
  latestData = keepLatest({
    value: () => this.fetchData.value,
    when: () => this.fetchData.isPending,
  })

  get feedbacks() {
    return this.latestData ?? []
  }

  <template>
    <div ...attributes>
      {{#if this.feedbacks.length}}
        <div>
          <h3 class="mb-2 text-center">{{t "pages.recentWriting.viewWriting.feedbacks.previousTeacherFeedback"}}</h3>
          {{#each this.feedbacks as |feedback|}}
            <TeacherFeedbackCard
              @date={{feedback.created_date}}
              @title={{t
                "pages.recentWriting.viewWriting.feedbacks.teacherFeedback"
                teacherName=feedback.teacher_full_name
              }}
              @mark={{feedback.mark}}
              @sticker={{feedback.sticker}}
              class="mb-6"
              id={{feedback.feedback_id}}
            >
              {{htmlSafe feedback.feedback_text}}
            </TeacherFeedbackCard>
          {{/each}}
        </div>
      {{/if}}

      <div class="mt-16">
        <h3 class="mb-2 text-center">{{t "pages.recentWriting.viewWriting.feedbacks.newTeacherFeedbackTitle"}}</h3>
        <TeacherFeedbackForm
          @themeKey={{@themeKey}}
          @studentId={{@studentId}}
          @resultId={{@resultId}}
          @snapshotId={{@snapshotId}}
          @onFeedbackSuccess={{this.fetchData.retry}}
        />
      </div>
    </div>
  </template>
}

export default TeacherFeedbacksSection
